import React from 'react';
import { useUserCanPerformAction } from '@/utils/hooks/useUserCanPerformAction';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import Switch from 'antd/lib/switch';
import Divider from 'antd/lib/divider';
import Tooltip from 'antd/lib/tooltip';
import Button from 'antd/lib/button';
import {
    HistoryOutlined,
    MinusCircleFilled,
    NumberOutlined,
    SettingFilled,
    SlidersOutlined,
    TeamOutlined,
} from '@ant-design/icons';
import { antGrey } from '../styles/globalStyles';
import { InlineStepConfigDescription } from '../common/InlineStepConfigDescription';
import { StepGraphStep } from '@/types/workflow';
import { Destination } from '@/types/destinations';
import {
    AppNotificationType,
    AppPlatform,
    AppStoreType,
    CommonNotification,
    SettingField,
    StepNotificationConfigOverride,
} from '../../types';
import { useNotificationSettingFields } from '@/utils/hooks/useNotificationSettingFields';
import {
    computeOverrideIndicators,
    effectiveMentionLabels,
    overriddenDestinationLabel,
    overrideSendsToPrimaryChannels,
    overriddenSettingLabels,
} from '../../helpers/notificationOverrideHelpers';
import { DISALLOWED_ACTION_ERROR_MESSAGE } from '../HOCs/PermissionedActionButton';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import Popconfirm from 'antd/lib/popconfirm';
import { AppSettingsV2Updatable } from '../../actions/updatable';
import { useIsLoading } from '../../utils/hooks/helpers';
import { useDispatch } from 'react-redux';
import { tryUpdateAppSettingsV2 } from '../../actions/appSettings';
import { DestinationCounter } from '../appHome/DestinationCounter';

interface NotificationStepConfigOverrideRowProps {
    appId: string;
    orgId: string;
    workflowId: string;
    notificationId: AppNotificationType;
    stepGraphStep: StepGraphStep;
    override: StepNotificationConfigOverride;
    appNotification: CommonNotification;
    group?: { name: string; stepConfigIds: string[] };
    destinations: Destination[];
    enabled: boolean;
    // If true, disables the toggle so the user can't fire repeated requests for the same key and trigger races with the loading-state reducer.
    isUpdating?: boolean;
    platform: AppPlatform;
    appStoreType: AppStoreType;
    availableChannelsById?: Record<string, string>;
    onClickSettings: () => void;
    onToggleEnabled?: (enabled: boolean) => void;
}

const ICON_BUTTON_PADDING = 8;

const overrideTooltipLabelStyle: React.CSSProperties = { color: antGrey.grey5 };

export const overrideChannelTooltipContent = (
    override: StepNotificationConfigOverride,
    appNotification: CommonNotification,
    resolveChannelName: (channel: { id: string; name?: string }) => string,
) => {
    const destinationLabel = overriddenDestinationLabel(override, appNotification);
    return (
        <div>
            <div style={overrideTooltipLabelStyle}>Channel override:</div>
            {(override.channels?.length ?? 0) > 0 && (
                <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                    {(override?.channels ?? []).map((c) => (
                        <li key={c.id}>{resolveChannelName(c)}</li>
                    ))}
                </ul>
            )}
            {overrideSendsToPrimaryChannels(override, appNotification) && (
                <div style={{ marginTop: 4 }}>Primary channel(s)</div>
            )}
            {destinationLabel && <div style={{ marginTop: 4 }}>Sent via {destinationLabel}</div>}
        </div>
    );
};

export const overrideSettingsTooltipContent = (
    override: StepNotificationConfigOverride,
    appNotification: CommonNotification,
    settingFields: SettingField[],
) => (
    <div>
        <div style={overrideTooltipLabelStyle}>Settings override:</div>
        <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
            {overriddenSettingLabels(override, appNotification, settingFields).map((label) => (
                <li key={label}>{label}</li>
            ))}
        </ul>
    </div>
);

export const overrideMentionTooltipContent = (
    override: StepNotificationConfigOverride,
    appNotification: CommonNotification,
) => {
    const mentionItems = effectiveMentionLabels(appNotification, override);
    return (
        <div>
            <div style={overrideTooltipLabelStyle}>@mention:</div>
            {mentionItems.length > 0 ?
                <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                    {mentionItems.map((m, i) => (
                        <li key={i}>{m}</li>
                    ))}
                </ul>
            :   <div style={{ marginTop: 4 }}>None</div>}
        </div>
    );
};

const NotificationStepConfigOverrideRow: React.FC<NotificationStepConfigOverrideRowProps> = ({
    appId,
    orgId,
    workflowId,
    notificationId,
    stepGraphStep,
    override,
    appNotification,
    group,
    destinations,
    enabled,
    isUpdating,
    platform,
    appStoreType,
    availableChannelsById,
    onClickSettings,
    onToggleEnabled,
}) => {
    const dispatch = useDispatch();
    const { settingFieldsByNotificationId } = useNotificationSettingFields(orgId, appId);
    const settingFields = settingFieldsByNotificationId?.[notificationId] ?? [];
    const deleteKey =
        group ? `${notificationId}#${group.name}#delete` : `${notificationId}#${stepGraphStep.stepConfigId}`;
    const isDeletingSingle = useIsLoading(AppSettingsV2Updatable.DeleteStepNotificationConfig, deleteKey);
    const isDeletingGroup = useIsLoading(AppSettingsV2Updatable.UpdateStepNotificationConfigs, deleteKey);
    const isDeleting = isDeletingSingle || isDeletingGroup;
    const userCanToggle = useUserCanPerformAction(RunwayUserAction.UAUpdateAppSettings);

    const resolveChannelName = (c: { id: string; name?: string }) => availableChannelsById?.[c.id] ?? c.name ?? c.id;

    const handleDelete = () => {
        if (group) {
            dispatch(
                tryUpdateAppSettingsV2(
                    AppSettingsV2Updatable.UpdateStepNotificationConfigs,
                    {
                        orgId,
                        appId,
                        workflowId,
                        notificationId,
                        stepConfigIds: [],
                        deletedStepConfigIds: group.stepConfigIds,
                    },
                    deleteKey,
                ),
            );
            return;
        }
        dispatch(
            tryUpdateAppSettingsV2(
                AppSettingsV2Updatable.DeleteStepNotificationConfig,
                { orgId, appId, workflowId, stepConfigId: stepGraphStep.stepConfigId, notificationId },
                deleteKey,
            ),
        );
    };

    const { hasChannelOverride, hasMentionOverride, hasTriggerOverride, hasSettingsOverride } =
        computeOverrideIndicators(override, appNotification, settingFields);
    const hasAnyOverrideIcon = hasChannelOverride || hasMentionOverride || hasTriggerOverride || hasSettingsOverride;

    const channelTooltip = overrideChannelTooltipContent(override, appNotification, resolveChannelName);
    const mentionTooltip = overrideMentionTooltipContent(override, appNotification);
    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexGrow: 1,
                backgroundColor: antGrey.grey2,
                // caret slot + notification icon + 18px gap, to sit under the parent's name
                padding: '8px 12px 8px 58px',
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {destinations.length > 0 && (
                    <DestinationCounter size={16} destinations={destinations} hideTooltip={!group} />
                )}
                {group ?
                    <Text style={{ color: antGrey.grey9 }}>{group.name}</Text>
                :   <InlineStepConfigDescription
                        stepGraphStep={stepGraphStep}
                        platform={platform}
                        appStoreType={appStoreType}
                        isWorkflowsCIStepEnabled
                    />
                }
            </div>
            <Space size={14} align="center">
                {hasChannelOverride && (
                    <Tooltip
                        title={channelTooltip}
                        placement="leftTop"
                        overlayStyle={{ maxWidth: 1000 }}
                        mouseEnterDelay={0}
                        mouseLeaveDelay={0}
                    >
                        <NumberOutlined style={{ color: antGrey.grey7, fontSize: 14 }} />
                    </Tooltip>
                )}
                {hasMentionOverride && (
                    <Tooltip
                        title={mentionTooltip}
                        placement="leftTop"
                        overlayStyle={{ maxWidth: 1000 }}
                        mouseEnterDelay={0}
                        mouseLeaveDelay={0}
                    >
                        <TeamOutlined style={{ color: antGrey.grey7, fontSize: 14 }} />
                    </Tooltip>
                )}
                {hasTriggerOverride && (
                    <Tooltip
                        title="Trigger override: customized trigger conditions"
                        placement="leftTop"
                        mouseEnterDelay={0}
                        mouseLeaveDelay={0}
                    >
                        <HistoryOutlined style={{ color: antGrey.grey7, fontSize: 14 }} />
                    </Tooltip>
                )}
                {hasSettingsOverride && (
                    <Tooltip
                        title={overrideSettingsTooltipContent(override, appNotification, settingFields)}
                        placement="leftTop"
                        overlayStyle={{ maxWidth: 1000 }}
                        mouseEnterDelay={0}
                        mouseLeaveDelay={0}
                    >
                        <SlidersOutlined style={{ color: antGrey.grey7, fontSize: 14 }} />
                    </Tooltip>
                )}
                <Divider
                    type="vertical"
                    style={{
                        margin: '0 8px',
                        height: 20,
                        borderColor: antGrey.grey6,
                        visibility: hasAnyOverrideIcon ? 'visible' : 'hidden',
                    }}
                />
                <Space size={24} align="center">
                    {userCanToggle ?
                        <Switch
                            checked={enabled}
                            disabled={!onToggleEnabled || isUpdating}
                            onChange={(checked, event) => {
                                event.stopPropagation();
                                onToggleEnabled?.(checked);
                            }}
                        />
                    :   <Tooltip title={DISALLOWED_ACTION_ERROR_MESSAGE} placement="topRight">
                            <span>
                                <Switch checked={enabled} disabled />
                            </span>
                        </Tooltip>
                    }
                    <Button
                        type="text"
                        style={{ marginInline: -ICON_BUTTON_PADDING }}
                        icon={<SettingFilled style={{ color: antGrey.grey7 }} />}
                        onClick={onClickSettings}
                    />
                    <Popconfirm
                        title={`Remove override for ${group ? group.name : stepGraphStep.stepName}?`}
                        description="The notification will use app-level defaults."
                        onConfirm={handleDelete}
                        okText="Remove"
                        okButtonProps={{ danger: true, disabled: !userCanToggle }}
                        placement="leftTop"
                    >
                        <Button
                            type="text"
                            style={{ marginInline: -ICON_BUTTON_PADDING }}
                            loading={isDeleting}
                            icon={<MinusCircleFilled style={{ color: antGrey.grey7 }} />}
                        />
                    </Popconfirm>
                </Space>
            </Space>
        </div>
    );
};

export default NotificationStepConfigOverrideRow;
